import { useContext, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { removalFromGarageRequested, vehicleSelected } from 'Core/actions/fitmentSearch/index.js';
import {
  createVehicleIsSelectedSelector,
  fitmentSearchBaseFieldsSelector,
  fitmentSearchExtraFieldsSelector,
} from 'Core/selectors/fitmentSearch/index.js';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig.js';
import { cloneSafe } from 'Utils/components.ts';
import { simpleHandler } from 'Utils/roleHandler.js';
import GarageContext from '../garageContext.ts';

import type { FunctionComponent } from 'react';
import type { TemplateFunction } from 'Components/types.ts';
import type { Vehicle } from 'Models/index.ts';

export interface Params {
  isSelected: boolean;
  vehicleString: string;
  vehicle: Record<string, string>;
  vehicleBaseValues: string[];
  vehicleExtraValues: string[];
}

type Props = {
  template: TemplateFunction<Params>;
  vehicle: Vehicle;
};

const GarageItem: FunctionComponent<Props> = ({ template, vehicle }) => {
  const dispatch = useDispatch();

  const { closeGarage, doNotRedirectOnVehicleSelect, redirectUrl } = useContext(GarageContext);

  const isSelectedSelector = useMemo(() => createVehicleIsSelectedSelector(vehicle), [vehicle]);

  const isSelected = useSelector(isSelectedSelector);
  const baseFitmentFields = useSelector(fitmentSearchBaseFieldsSelector) as string[];
  const extraFitmentFields = useSelector(fitmentSearchExtraFieldsSelector) as string[];

  const removeVehicle = () => {
    dispatch(removalFromGarageRequested(vehicle, isSelected));
    fitmentSearchConfig.onVehicleDiscarded();
  };

  const selectVehicle = () => {
    closeGarage?.();

    dispatch(
      vehicleSelected(vehicle, {
        doNotRedirectOnVehicleSelect,
        isSelectedFromGarage: true,
        redirectUrl,
        sendIsolatedRequest: true,
      }),
    );

    fitmentSearchConfig.onVehicleSelected(vehicle.filteredFieldMap);
  };

  const onClick = simpleHandler({ removeVehicle, selectVehicle });

  const component = template.call({
    isSelected,
    vehicleString: vehicle.toString(),
    vehicle: vehicle.filteredFieldMap,
    vehicleBaseValues: vehicle.getFilteredValues(baseFitmentFields),
    vehicleExtraValues: vehicle.getFilteredValues(extraFitmentFields),
  });
  return cloneSafe(component, null, { onClick });
};

export default GarageItem;
